import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import UIElement from '@ckeditor/ckeditor5-engine/src/view/uielement';
import { toWidget } from '@ckeditor/ckeditor5-widget/src/utils';
import { snakeCase } from 'lodash';
import React from 'react';
import ReactDOM from 'react-dom';
import { IntlProvider } from 'react-intl';
import { QueryClientProvider } from 'react-query';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { AiFillAppDestination } from 'api/ai-fill/types';
import { theme } from 'components/styles/theme';
import { store } from 'data';
import { PrereleaseFeature } from 'hooks/use-feature';
import { queryClient } from 'queries';
import translations from 'translations/en.json';
import { AIFillApp } from 'vendor/ckeditor/react-components/ai-fill/app';
import { AIFillAppResolvedContent } from 'vendor/ckeditor/react-components/ai-fill/app-content';
import { AIFillResolveApp } from 'vendor/ckeditor/react-components/ai-fill/resolve-app';
import { AIFillSelector } from './ai-fill-selector';
import styles from './ai-fill.module.css';
import { AIFillInsertCommand } from './insert-command';
import { AIFillResolveCommand } from './resolve-command';
import { AIFillResolvedCommand } from './resolved-command';

export class AIFill extends Plugin {
  static get pluginName(): string {
    return 'AIFill';
  }

  private aiFillApp(appId, asReadOnly, uniqueId, container) {
    const { editor } = this;

    ReactDOM.render(
      <Provider store={store}>
        <BrowserRouter>
          <ThemeProvider theme={theme}>
            <IntlProvider locale="en" messages={translations}>
              <QueryClientProvider client={queryClient}>
                <AIFillApp
                  appId={appId}
                  asReadOnly={asReadOnly}
                  uniqueId={uniqueId}
                  editor={editor}
                />
              </QueryClientProvider>
            </IntlProvider>
          </ThemeProvider>
        </BrowserRouter>
      </Provider>,
      container,
    );
  }

  private aiFillResolveApp({
    appId,
    meetingIds,
    uniqueId,
    destination,
    container,
  }) {
    const { editor } = this;

    ReactDOM.render(
      <Provider store={store}>
        <BrowserRouter>
          <ThemeProvider theme={theme}>
            <IntlProvider locale="en" messages={translations}>
              <QueryClientProvider client={queryClient}>
                <AIFillResolveApp
                  appId={appId}
                  uniqueId={uniqueId}
                  editor={editor}
                  sourceMeetingIds={meetingIds}
                  destination={destination}
                />
              </QueryClientProvider>
            </IntlProvider>
          </ThemeProvider>
        </BrowserRouter>
      </Provider>,
      container,
    );
  }

  private aiFillResolvedContent(appId, uniqueId, container) {
    const { editor } = this;

    ReactDOM.render(
      <Provider store={store}>
        <BrowserRouter>
          <ThemeProvider theme={theme}>
            <IntlProvider locale="en" messages={translations}>
              <QueryClientProvider client={queryClient}>
                <AIFillAppResolvedContent
                  appId={appId}
                  uniqueId={uniqueId}
                  editor={editor}
                />
              </QueryClientProvider>
            </IntlProvider>
          </ThemeProvider>
        </BrowserRouter>
      </Provider>,
      container,
    );
  }

  static get requires() {
    try {
      const meetingsAIEnabled = JSON.parse(
        localStorage.getItem(
          `__features.v2.${snakeCase(PrereleaseFeature.MeetingsAI)}`,
        ) ?? 'false',
      );

      return meetingsAIEnabled ? [AIFillSelector] : [];
    } catch {
      return [];
    }
  }

  init() {
    this.editor.config.define('aiFill', { addAsPlaceholderOnly: true });
    this.defineSchema();
    this.defineConverters();
    this.editor.commands.add(
      'ai-fill-insert',
      new AIFillInsertCommand(this.editor),
    );
    this.editor.commands.add(
      'ai-fill-resolve',
      new AIFillResolveCommand(this.editor),
    );
    this.editor.commands.add(
      'ai-fill-resolved',
      new AIFillResolvedCommand(this.editor),
    );
  }

  private defineSchema() {
    const { schema } = this.editor.model;

    schema.register('aiFill', {
      isObject: true,
      classes: ['placeholder'],
      allowWhere: '$block',
      allowAttributes: ['appId', 'asReadOnly', 'uniqueId'],
    });
    schema.register('aiFillResolving', {
      isObject: true,
      classes: ['resolving'],
      allowWhere: '$block',
      allowAttributes: ['appId', 'uniqueId', 'sourceMeetingIds'],
    });
    schema.register('aiFillResolved', {
      isObject: true,
      classes: ['resolved'],
      allowWhere: '$block',
      allowAttributes: ['appId', 'uniqueId', 'sourceMeetingIds'],
    });
  }

  private defineConverters() {
    const renderAIFillApp = this.aiFillApp.bind(this);
    const renderAIFillResolveApp = this.aiFillResolveApp.bind(this);
    const renderAIFillResolvedContent = this.aiFillResolvedContent.bind(this);
    const { addAsPlaceholderOnly, destination } =
      this.editor.config.get<{
        addAsPlaceholderOnly: boolean;
        destination: AiFillAppDestination;
      }>('aiFill');

    this.editor.conversion.for('upcast').elementToElement({
      view: {
        name: 'div',
        classes: 'placeholder',
      },
      model: (viewElement, { writer: modelWriter }) => {
        return modelWriter.createElement('aiFill', {
          appId: viewElement.getAttribute('data-ai-fill-app-id'),
          asReadOnly: addAsPlaceholderOnly,
          uniqueId: viewElement.getAttribute('data-ai-fill-unique-id'),
        });
      },
    });
    this.editor.conversion.for('editingDowncast').elementToElement({
      model: 'aiFill',
      view: (modelItem, { writer: viewWriter }) => {
        const appId = modelItem.getAttribute('appId');
        const uniqueId = modelItem.getAttribute('uniqueId');
        const reactWrapper = viewWriter.createUIElement(
          'div',
          {},
          function (this: UIElement, domDocument) {
            const domElement = this.toDomElement(domDocument);

            renderAIFillApp(appId, addAsPlaceholderOnly, uniqueId, domElement);

            return domElement;
          },
        );
        const view = viewWriter.createContainerElement(
          'div',
          {
            class: `placeholder ${styles.section}`,
            'data-ai-fill-app-id': appId,
            'data-ai-fill-as-read-only': addAsPlaceholderOnly,
            'data-ai-fill-unique-id': uniqueId,
            'data-cke-ignore-events': true,
          },
          [reactWrapper],
        );

        return toWidget(view, viewWriter);
      },
    });
    this.editor.conversion.for('dataDowncast').elementToElement({
      model: 'aiFill',
      view: (modelItem, { writer: viewWriter }) => {
        return viewWriter.createContainerElement('div', {
          class: `placeholder ${styles.section}`,
          'data-ai-fill-app-id': modelItem.getAttribute('appId'),
          'data-ai-fill-unique-id': modelItem.getAttribute('uniqueId'),
        });
      },
    });
    this.editor.conversion.for('upcast').elementToElement({
      view: {
        name: 'section',
        classes: 'resolving',
      },
      model: (viewElement, { writer: modelWriter }) => {
        return modelWriter.createElement('aiFillResolving', {
          appId: viewElement.getAttribute('data-ai-fill-app-id'),
          uniqueId: viewElement.getAttribute('data-ai-fill-unique-id'),
          sourceMeetingIds: viewElement.getAttribute(
            'data-ai-fill-source-meetings',
          ),
        });
      },
    });
    this.editor.conversion.for('editingDowncast').elementToElement({
      model: 'aiFillResolving',
      view: (modelItem, { writer: viewWriter }) => {
        const appId = modelItem.getAttribute('appId');
        const uniqueId = modelItem.getAttribute('uniqueId');
        const meetingIds = modelItem.getAttribute('sourceMeetingIds');
        const reactWrapper = viewWriter.createUIElement(
          'div',
          {},
          function (this: UIElement, domDocument) {
            const domElement = this.toDomElement(domDocument);

            renderAIFillResolveApp({
              appId,
              meetingIds: meetingIds.split(',').map(Number),
              uniqueId,
              container: domElement,
              destination,
            });

            return domElement;
          },
        );
        const view = viewWriter.createContainerElement(
          'section',
          {
            class: `resolving ${styles.section}`,
            'data-ai-fill-app-id': appId,
            'data-ai-fill-source-meetings': meetingIds,
            'data-ai-fill-unique-id': uniqueId,
            'data-cke-ignore-events': true,
          },
          [reactWrapper],
        );

        return toWidget(view, viewWriter);
      },
    });
    this.editor.conversion.for('dataDowncast').elementToElement({
      model: 'aiFillResolving',
      view: (modelItem, { writer: viewWriter }) => {
        return viewWriter.createContainerElement('section', {
          class: `resolving ${styles.section}`,
          'data-ai-fill-app-id': modelItem.getAttribute('appId'),
          'data-ai-fill-source-meetings':
            modelItem.getAttribute('sourceMeetingIds'),
          'data-ai-fill-unique-id': modelItem.getAttribute('uniqueId'),
        });
      },
    });
    this.editor.conversion.for('upcast').elementToElement({
      view: {
        name: 'div',
        classes: 'resolved',
      },
      model: (viewElement, { writer: modelWriter }) => {
        return modelWriter.createElement('aiFillResolved', {
          appId: viewElement.getAttribute('data-ai-fill-app-id'),
          uniqueId: viewElement.getAttribute('data-ai-fill-unique-id'),
          sourceMeetingIds: viewElement.getAttribute(
            'data-ai-fill-source-meetings',
          ),
        });
      },
    });
    this.editor.conversion.for('editingDowncast').elementToElement({
      model: 'aiFillResolved',
      view: (modelItem, { writer: viewWriter }) => {
        const appId = modelItem.getAttribute('appId');
        const uniqueId = modelItem.getAttribute('uniqueId');
        const meetingIds = modelItem.getAttribute('sourceMeetingIds');
        const reactWrapper = viewWriter.createUIElement(
          'div',
          {},
          function (this: UIElement, domDocument) {
            const domElement = this.toDomElement(domDocument);

            renderAIFillResolvedContent(appId, uniqueId, domElement);

            return domElement;
          },
        );
        const view = viewWriter.createContainerElement(
          'div',
          {
            class: `${styles.section}`,
            'data-ai-fill-app-id': appId,
            'data-ai-fill-source-meetings': meetingIds,
            'data-ai-fill-unique-id': uniqueId,
            'data-cke-ignore-events': true,
          },
          [reactWrapper],
        );

        return toWidget(view, viewWriter);
      },
    });
    this.editor.conversion.for('dataDowncast').elementToElement({
      model: 'aiFillResolved',
      view: (modelItem, { writer: viewWriter }) => {
        return viewWriter.createContainerElement('div', {
          'data-ai-fill-app-id': modelItem.getAttribute('appId'),
          'data-ai-fill-unique-id': modelItem.getAttribute('uniqueId'),
          'data-ai-fill-source-meetings':
            modelItem.getAttribute('sourceMeetingIds'),
        });
      },
    });
  }
}
